import React from 'react';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/core';
import { withTheme } from 'emotion-theming';
import reset from 'emotion-reset';
import { breakpoint } from '@styles/breakpoints';

export const SIDEBAR_WIDTH = 300;

export const ActionButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  width: 32px;
  height: 32px;
  transition: background-color 0.3s ease-in-out;
  flex-shrink: 0;

  img {
    display: block;
    width: 16px;
    height: 16px;
    object-fit: contain;
    margin: 0;
  }

  &:hover {
    background-color: #3890a9;
  }

  ${({ large }) =>
    large
      ? css`
          width: 60px;
          height: 60px;

          img {
            width: 32px;
            height: 32px;
          }
        `
      : null}
`;

export const PageCenterWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
`;

export const Page = styled.div`
  display: flex;
  padding: 0 30px 0 ${SIDEBAR_WIDTH}px;
`;

export const Box = styled.div`
  padding: ${({ padding }) => padding || 60}px;
  background-color: #fff;
  margin: 30px 0;
  border-radius: 10px;
  box-shadow: 1px 3px 3px rgba(0, 0, 0, 0.05);
  width: 90%;
  max-width: ${({ fullwidth }) => (fullwidth ? 'unset' : '500px')};
  ${({ fullwidth }) => (fullwidth ? 'flex: 1;' : null)}}
`;

export const FlexListBox = styled(Box)`
  display: flex;
  flex-direction: column;
  flex-wrap: ${({ view }) => (view === 'list' ? 'nowrap' : 'wrap')};
  justify-content: space-between;
  padding: 0;
  position: relative;
  overflow: ${({ loading }) => (loading ? 'hidden' : 'visible')};
  height: calc(100vh - 60px);

  &::after {
    flex: auto;
    content: '';
  }
`;

export const List = styled.div`
  flex: 1 1 100%;
  overflow-y: scroll;
  ${({ view }) => {
    if (view === 'grid') {
      return css`
        display: flex;
        flex-wrap: wrap;
        flex: 1 1 calc(100% - 111px);
        padding: 20px 0;
        align-content: flex-start;
      `;
    }
    return null;
  }}
`;

export const Pagination = styled.div`
  z-index: 2;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.searchBar};
  display: flex;
  justify-content: flex-end;
  color: #fff;
  padding: 15px 30px;
  width: 100%;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;

  ul {
    display: flex;

    li {
      margin: 0 3px;

      a {
        font-size: 14px;
        display: block;
        cursor: pointer;

        &:focus {
          outline: none;
        }
      }

      &.previous {
        margin-right: 18px;

        a {
          background-image: url('/images/icons/previous.svg');
        }
      }

      &.next {
        margin-left: 18px;

        a {
          background-image: url('/images/icons/next.svg');
        }
      }

      &.previous,
      &.next {
        text-indent: -9999px;

        a {
          background-position: 50% 50%;
          background-repeat: no-repeat;
          background-size: contain;
          height: 16px;
          width: 16px;
        }

        &.disabled {
          opacity: 0.3;
        }
      }

      &.selected {
        a {
          color: ${({ theme }) => theme.colors.primary};
          font-weight: 600;
        }
      }
    }
  }
`;

export const Label = styled.div`
  color: #49b6d5;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
`;

const GlobalStyle = ({ theme }) => (
  <Global
    styles={css`
      ${reset}

      @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@100;300;400;500;700&display=swap');

      * {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      html {
        box-sizing: border-box;
      }

      *,
      *:before,
      *:after {
        box-sizing: inherit;
      }

      body {
        min-height: 100vh;
        font-family: 'Roboto', sans-serif;
        padding: 0;
        margin: 0;
        background: rgb(237, 242, 247);
        background: linear-gradient(
          0deg,
          rgba(237, 242, 247, 1) 15%,
          rgba(73, 182, 213, 0.43601190476190477) 56%,
          rgba(237, 242, 247, 1) 71%
        );
      }

      a {
        text-decoration: none;
      }

      b {
        font-weight: 500;
      }

      button {
        border: none;
        background-color: transparent;
        cursor: pointer;

        &:focus {
          outline: none;
        }
      }

      .Toastify__toast-container {
        width: calc(100% - 30px);
        top: 15px;
        left: 15px;
        transform: none;
        padding: 0;
      }

      .Toastify__toast {
        border-radius: 6px;
        padding: 24px 24px 24px 30px;

        small {
          font-size: 12px;
        }

        @media ${breakpoint.md} {
          padding: 15px 15px 15px 30px;
        }
      }

      .Toastify__close-button {
        align-self: center;
      }

      .Toastify__toast-body {
        font-size: 20px;
        line-height: 30px;
        letter-spacing: -0.3px;

        @media ${breakpoint.md} {
          font-size: 15px;
          line-height: 20px;
        }
      }

      .Toastify__progress-bar {
        height: 2px;
      }

      .Toastify__toast--info {
        background: ${theme.palette.base};
      }

      .Toastify__toast--error {
        background: #e15555;
      }

      .Toastify__toast--success {
        background: #27ae60;
      }
    `}
  />
);

export default withTheme(GlobalStyle);
